import {
  Box,
  Flex,
  FlexProps,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  TabsProps,
} from "@chakra-ui/react";
import React, { PropsWithChildren, useLayoutEffect, useState } from "react";

import { asArray } from "../../../utils/array";

interface SidebarTabProps<T extends string> {
  id: T;
  displayName: JSX.Element | string;
  padBottom?: true;
  selectedColor?: string;
}

export const SidebarTab = <T extends string>({
  children,
  padBottom,
}: PropsWithChildren<SidebarTabProps<T>>): JSX.Element => {
  if (padBottom)
    return (
      <Box pb="16" height="100%">
        {children}
      </Box>
    );
  return <>{children}</>;
};

type SidebarTab<T extends string> = React.ReactElement<SidebarTabProps<T>>;
type Child<T extends string> =
  | SidebarTab<T>
  | string
  | number
  | boolean
  | null
  | undefined;

interface SidebarTabsProps<T extends string>
  extends Omit<TabsProps, "index" | "onChange" | "defaultIndex"> {
  currentTab?: T;
  onTabChange?(tab: T): void;
  children: Child<T>[] | Child<T>;
  tabListWrapperProps?: FlexProps;
  variant?: "enclosed" | "underlined";
}

const SidebarTabs = <T extends string>({
  currentTab,
  onTabChange,
  isLazy = true,
  lazyBehavior = "keepMounted",
  children,
  tabListWrapperProps,
  variant = "enclosed",
  ...tabsProps
}: SidebarTabsProps<T>): JSX.Element => {
  const { tabs, tabIds, tabNames, tabSelectedColors } = useTabData(children);

  const [tabIndex, setTabIndexState] = useState(0);
  const setTabIndex = (idx: number): void => {
    if (idx !== tabIndex) {
      setTabIndexState(idx);
      onTabChange?.(tabIds[idx]);
    }
  };

  // Set tab based on the `currentTab` prop
  // useLayoutEffect to avoid one frame of rendering the prior tab
  useLayoutEffect(() => {
    if (currentTab) {
      const idx = tabIds.findIndex((tabId) => tabId === currentTab);
      if (idx !== -1) {
        setTabIndex(idx);
      }
    }
  }, [currentTab, tabIds]);

  return (
    <Tabs
      display="flex"
      flexDir="column"
      height="100%"
      index={tabIndex}
      onChange={setTabIndex}
      isLazy={isLazy}
      lazyBehavior={lazyBehavior}
      {...tabsProps}
    >
      <Flex alignItems="center" pt={6} px={6} {...tabListWrapperProps}>
        <TabList
          alignItems="center"
          bg="white"
          borderRadius="0"
          borderBottom="1px"
          borderColor="gray.200"
          display="flex"
          justifyContent="flex-start"
          overflow="hidden"
          flex="1"
        >
          {tabNames.map((tabName, idx) => {
            const selectedColor = tabSelectedColors[idx] ?? "blue.600";
            return (
              <Tab
                whiteSpace="nowrap"
                data-testid={`${tabIds[idx]}-tab`}
                py={4}
                px={3.5}
                key={tabIds[idx]}
                fontWeight="500"
                _hover={{
                  backgroundColor: "gray.100",
                }}
                _selected={{
                  color: `${selectedColor} !important`,
                  fontWeight: "600",
                  borderBottom: "3px solid",
                  borderColor: `${selectedColor} !important`,
                  borderRadius: "none",
                }}
                background="white"
                borderRadius="none"
                h="10"
                mb="-1px"
              >
                <Box fontSize="14px" lineHeight="20px">
                  {typeof tabName === "function"
                    ? tabName({ isSelected: idx === tabIndex })
                    : tabName}
                </Box>
              </Tab>
            );
          })}
        </TabList>
      </Flex>

      <TabPanels overflow="hidden" flex="1" minH="0" px={6} pb={6}>
        {tabs.map((tab, idx) => (
          <TabPanel
            key={tabIds[idx]}
            h="100%"
            minH="0"
            display="flex"
            flexDir="column"
          >
            {tab}
          </TabPanel>
        ))}
      </TabPanels>
    </Tabs>
  );
};

export default SidebarTabs;

/**
 * Filter out falsy children and return tabs along with names and ids
 */
function useTabData<T extends string>(
  children: Child<T>[] | Child<T>
): {
  tabs: SidebarTab<T>[];
  tabIds: T[];
  tabNames: (string | JSX.Element | React.FC<{ isSelected: boolean }>)[];
  tabSelectedColors: (string | undefined)[];
} {
  const tabs = asArray(children).filter((c) => !!c) as SidebarTab<T>[];

  const [tabIds, tabNames, tabSelectedColors] = tabs.reduce(
    ([ids, names, colors], tab) => [
      ids.concat(tab.props.id),
      names.concat(tab.props.displayName),
      colors.concat(tab.props.selectedColor),
    ],
    [[], [], []] as [
      T[],
      (JSX.Element | string | React.FC<{ isSelected: boolean }>)[],
      (string | undefined)[]
    ]
  );

  return { tabs, tabIds, tabNames, tabSelectedColors };
}
